import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../auth/useAuth';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { user, isAuthChecked } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // טעינת רשימת הפרויקטים
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/projects');
      setProjects(response.data);
      
      // טעינת הפרויקט הפעיל
      const activeResponse = await api.get('/projects/active');
      
      if (activeResponse.data) {
        setActiveProject(activeResponse.data);
      } else {
        // אם אין פרויקט פעיל
        if (user?.role === 'manager') {
          // מנהלים - הגדר כ"כל הפרויקטים"
          setActiveProject({ id: 'all', name: 'כל הפרויקטים' });
        } else {
          // סדרנים - אין פרויקט פעיל
          setActiveProject(null);
        }
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('שגיאה בטעינת הפרויקטים');
      setProjects([]);
      setActiveProject(null);
    } finally {
      setLoading(false);
    }
  };

  // החלפת פרויקט פעיל
  const switchProject = async (projectId) => {
    try {
      console.log('🔄 ProjectContext - switchProject started');
      console.log('🔄 Input projectId:', projectId);
      console.log('🔄 Available projects:', projects.map(p => ({ id: p.id, name: p.name })));
      
      setLoading(true);
      setError(null);
      
      // אם נבחר "כל הפרויקטים" או "לא משויכים", נשלח null כדי לבטל פרויקט פעיל
      const actualProjectId = (projectId === 'all' || projectId === 'unassigned') ? null : projectId;
      console.log('🔄 Converted actualProjectId:', actualProjectId);
      
      console.log('🔄 Making API call to /projects/set-active');
      const response = await api.post('/projects/set-active', { projectId: actualProjectId });
      console.log('🔄 API response:', response.status, response.data);
      
      // עדכון הפרויקט הפעיל
      let newActiveProject;
      if (projectId === 'all') {
        newActiveProject = { id: 'all', name: 'כל הפרויקטים' };
        console.log('🔄 Setting to "כל הפרויקטים"');
      } else if (projectId === 'unassigned') {
        newActiveProject = { id: 'unassigned', name: 'לא משויכים' };
        console.log('🔄 Setting to "לא משויכים"');
      } else {
        const project = projects.find(p => p.id === projectId);
        newActiveProject = project;
        console.log('🔄 Setting to specific project:', project);
      }
      
      console.log('🔄 About to set activeProject to:', newActiveProject);
      setActiveProject(newActiveProject);
      console.log('🔄 setActiveProject called');
      
      // הרענון יתבצע אוטומטית דרך useEffect ברכיבים שמאזינים לשינוי activeProject
      
    } catch (error) {
      console.error('🔄 Error switching project:', error);
      console.error('🔄 Error details:', error.response?.data);
      setError('שגיאה בהחלפת הפרויקט');
      throw error;
    } finally {
      console.log('🔄 Setting loading to false');
      setLoading(false);
    }
  };

  // יצירת פרויקט חדש
  const createProject = async (projectData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/projects', projectData);
      const newProject = response.data;
      
      setProjects(prev => [newProject, ...prev]);
      
      return newProject;
    } catch (error) {
      console.error('Error creating project:', error);
      setError('שגיאה ביצירת הפרויקט');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // עדכון פרויקט
  const updateProject = async (projectId, projectData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.patch(`/projects/${projectId}`, projectData);
      const updatedProject = response.data;
      
      setProjects(prev => 
        prev.map(p => p.id === projectId ? updatedProject : p)
      );
      
      if (activeProject && activeProject.id === projectId) {
        setActiveProject(updatedProject);
      }
      
      return updatedProject;
    } catch (error) {
      console.error('Error updating project:', error);
      setError('שגיאה בעדכון הפרויקט');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // מחיקת פרויקט
  const deleteProject = async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      
      await api.delete(`/projects/${projectId}`);
      
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      if (activeProject && activeProject.id === projectId) {
        setActiveProject(null);
      }
      
    } catch (error) {
      console.error('Error deleting project:', error);
      setError('שגיאה במחיקת הפרויקט');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // טעינה ראשונית - רק אם המשתמש מחובר
  useEffect(() => {
    if (isAuthChecked && user) {
      fetchProjects();
    }
  }, [isAuthChecked, user]);

  const value = {
    projects,
    activeProject,
    loading,
    error,
    fetchProjects,
    switchProject,
    createProject,
    updateProject,
    deleteProject,
    setError
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
